import './chart-card.scss';
import { FC, ReactNode } from 'react';
import { InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import { CHART_HEIGHT } from '../../charts/chart-commons';

export type ChartAccent = 'blue' | 'green' | 'red' | 'amber' | 'purple' | 'cyan';

export interface ChartStat {
    label: string;
    value: string;
    color?: string;
}

interface Props {
    title: string;
    subtitle?: string;
    icon?: ReactNode;
    accent?: ChartAccent;
    /** در حال دریافت داده */
    loading?: boolean;
    /** داده‌ای برای نمایش وجود ندارد */
    empty?: boolean;
    /** چیپ‌های خلاصه بازه در هدر کارت */
    stats?: ChartStat[];
    /** کارت تمام‌عرض در گرید */
    wide?: boolean;
    children: ReactNode;
}

const SKELETON_BARS = [42, 68, 54, 83, 60, 92, 48, 74, 64, 88, 56, 79];

const ChartSkeleton: FC = () => (
    <div className="chart-skeleton" style={{ height: CHART_HEIGHT }}>
        {SKELETON_BARS.map((height, index) => (
            <span key={index} style={{ height: `${height}%`, animationDelay: `${index * 0.08}s` }} />
        ))}
    </div>
);

const EmptyState: FC = () => (
    <div className="chart-empty" style={{ height: CHART_HEIGHT }}>
        <span className="icon">
            <InboxOutlined />
        </span>
        <p className="title">داده‌ای موجود نیست</p>
        <p className="description">برای بازه زمانی انتخاب شده داده‌ای یافت نشد.</p>
    </div>
);

export const ChartCard: FC<Props> = ({
    title,
    subtitle,
    icon,
    accent = 'blue',
    loading,
    empty,
    stats,
    wide,
    children,
}) => {
    const showSkeleton = loading && empty;
    const refreshing = loading && !empty;

    return (
        <section className={`pmx-chart-card accent-${accent} ${wide ? 'wide' : ''}`}>
            <header>
                <div className="heading">
                    {icon && <span className="icon">{icon}</span>}
                    <div className="titles">
                        <h4>{title}</h4>
                        {subtitle && <p>{subtitle}</p>}
                    </div>
                </div>
                {!!stats?.length && !showSkeleton && !empty && (
                    <div className="stats">
                        {stats.map((stat) => (
                            <span className="stat" key={stat.label}>
                                <i className="dot" style={{ background: stat.color }} />
                                <span className="stat-label">{stat.label}</span>
                                <b>{stat.value}</b>
                            </span>
                        ))}
                    </div>
                )}
            </header>

            <div className={`body ${refreshing ? 'refreshing' : ''}`}>
                {showSkeleton ? <ChartSkeleton /> : empty ? <EmptyState /> : children}
                {refreshing && (
                    <span className="refresh-badge">
                        <LoadingOutlined /> در حال به‌روزرسانی
                    </span>
                )}
            </div>
        </section>
    );
};
